import {debounceTime, Options, TableDataRequest} from '@fl/cmsch-fe-library';
import {debounce, isEmpty, isNumber} from 'lodash/fp';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {none, Opt, opt} from 'ts-opt';

import {ColumnHeader} from 'api/gen/ColumnHeader';
import {UserProfileOtherSubjectRole} from 'api/gen/UserProfileOtherSubjectRole';
import {TFunction, useOurTranslation} from 'app/translations';
import {Errors} from 'utils/validator';

import {managementOtherSubjectsRowMapper} from '../components/management-other-subjects-table/row-mapper';
import {adminAction} from '../model';
import {
    managementOtherSubjectsRoleOptionsSelector,
    managementOtherSubjectsUserOptionsSelector,
    simpleAdminSelector,
} from '../model/selector';
import {CreateOrEditOrNull} from '../types/create-or-edit-or-null';
import {ManagementOtherSubjectFormValues} from '../types/management-other-subject-form-values';
import {ManagementOtherSubjectsTableType} from '../types/management-other-subjects';
import {defaultUserProfileAndRoleIds, UserProfileAndRole} from '../types/user-profile-and-role-ids';
import {managementOtherSubjectsValidate} from '../utils/management-other-subjects-validate';

const otherSubjectForm = 'otherSubjectForm';

interface UseManagementOtherSubjectSetup {
    otherSubjectId: Opt<number>;
    otherSubjectsOptions: Options<number>;
    managementOtherSubjectsExtraColumns: Array<ColumnHeader>;
    roleOptions: Options<number>;
    userProfileOptions: Options<number>;
    userProfileAndRoles: UserProfileAndRole;
    visibleModal: boolean;
    modalTitle: string;
    otherSubjectDetail: Opt<UserProfileOtherSubjectRole>;
    isEdit: boolean;
    t: TFunction<'admin'>;
    tCommon: TFunction<'common'>;
    searchManagementOtherSubjects(text: string): void;
    handleManagementOtherSubjectSelectChangeValue(value: Opt<number>): void;
    onManagementOtherSubjectsTableFetch(
        tableDataRequest: Opt<TableDataRequest<ManagementOtherSubjectsTableType>>
    ): void;
    removeRolesForUser(_id: number, user: ManagementOtherSubjectsTableType): void;
    removeUsersFromRole(columnRoleId: string): void;
    removeConfirmTitle(_id: number, user: ManagementOtherSubjectsTableType): string;
    handleColumnOpenCreateModal(roleId: string, roleName: string): void;
    handleRowOpenCreateModal(_id: number, record: ManagementOtherSubjectsTableType): void;
    handleOpenCreateModalWithoutLimitations(): void;
    handleSubmitModal(): void;
    handleUserSearch(searchText: string): void;
    handleRoleSearch(searchText: string): void;
    handleCloseModal(): void;
    validateDebounced(): void;
    validate(values: ManagementOtherSubjectFormValues): Errors<ManagementOtherSubjectFormValues>;
    rowMapper(row: ManagementOtherSubjectsTableType): ManagementOtherSubjectsTableType;
    clearOtherSubjectDetail(): void;
}

/** Builds the state and callbacks used by the other-subject management screen. */
// eslint-disable-next-line max-lines-per-function
export const useManagementOtherSubjectSetup = (): UseManagementOtherSubjectSetup => {
    const dispatch = useDispatch();
    const {t, tCommon} = useOurTranslation('admin');

    const [otherSubjectId, setOtherSubjectId] = useState<Opt<number>>(opt(undefined));
    const [userProfileAndRoles, setUserProfileAndRoleIds] = useState<UserProfileAndRole>(defaultUserProfileAndRoleIds);
    const [actionMode, setActionMode] = useState<CreateOrEditOrNull>(null);

    const otherSubjectsOptions = useSelector(simpleAdminSelector.managementOtherSubjectsOptions);
    const subjectManagementModalVisible = useSelector(simpleAdminSelector.subjectManagementModalVisible);
    const otherSubjectDetail = useSelector(simpleAdminSelector.managementOtherSubjectDetail);
    const roleOptions = useSelector(managementOtherSubjectsRoleOptionsSelector);
    const userProfileOptions = useSelector(managementOtherSubjectsUserOptionsSelector);
    const managementOtherSubjectsExtraColumns = useSelector(simpleAdminSelector.managementOtherSubjectsExtraColumns);

    useEffect(() => {
        if (!isEmpty(otherSubjectsOptions) && otherSubjectsOptions.length === 1) {
            const firstOption = opt(otherSubjectsOptions).headIn().prop('value');
            setOtherSubjectId(opt(otherSubjectsOptions).headIn().prop('value'));
            dispatch(adminAction.getManagementOtherSubjectsTableData(opt({action: 'reset'}), firstOption));
        }
    }, [otherSubjectsOptions, dispatch]);

    const isEdit = useMemo(() => actionMode === 'edit', [actionMode]);
    const visibleModal = useMemo(() =>
        subjectManagementModalVisible === otherSubjectForm && (!isEdit || otherSubjectDetail.isFull),
    [subjectManagementModalVisible, otherSubjectDetail, isEdit]);
    const otherSubjectName = useMemo(() =>
        otherSubjectId.isFull ? otherSubjectsOptions.find(x => x.value === otherSubjectId.orUndef())?.label : '',
    [otherSubjectId, otherSubjectsOptions]);
    const modalTitle = useMemo(() => t(isEdit
        ? 'updateAccessToOtherSubject'
        : 'addAccessToOtherSubject',
    {otherSubject: otherSubjectName}),
    [t, otherSubjectName, isEdit]);

    const validateData = useCallback(() => {
        dispatch(adminAction.validateManagementOtherSubject());
    }, [dispatch]);
    const validateDebounced = useMemo(() => debounce(debounceTime)(validateData), [validateData]);

    const searchManagementOtherSubjects = useCallback((text: string) => {
        dispatch(adminAction.getManagementOtherSubjectsOptions(text));
    }, [dispatch]);

    const handleManagementOtherSubjectSelectChangeValue = useCallback((value: Opt<number>) => {
        dispatch(adminAction.getManagementOtherSubjectsTableData(opt({action: 'reset'}), value));
        setOtherSubjectId(value);
    }, [dispatch]);

    const onManagementOtherSubjectsTableFetch = useCallback((
        tableDataRequest: Opt<TableDataRequest<ManagementOtherSubjectsTableType>>,
    ) => {
        if (tableDataRequest.isFull) {
            dispatch(adminAction.getManagementOtherSubjectsTableData(tableDataRequest, otherSubjectId));
        }
    }, [otherSubjectId, dispatch]);

    const removeRolesForUser = useCallback((_id: number, user: ManagementOtherSubjectsTableType) => {
        if (otherSubjectId.isFull) {
            dispatch(adminAction.removeOtherSubjectRolesForUser(
                user.userProfileId,
                otherSubjectId.orCrash('OtherSubject id not found for removing user roles'),
            ));
        }
    }, [dispatch, otherSubjectId]);

    const clearOtherSubjectDetail = useCallback(() => {
        dispatch(adminAction.getManagementOtherSubjectDetailSuccess(none));
    }, [dispatch]);

    const removeUsersFromRole = useCallback((columnRoleId: string) => {
        const roleIdNarrowed = opt(columnRoleId).map(Number).narrow(isNumber);
        if (otherSubjectId.isFull && roleIdNarrowed.isFull) {
            dispatch(adminAction.removeUsersFromOtherSubjectRole(
                roleIdNarrowed.orCrash('Role ID is not a number'),
                otherSubjectId.orCrash('OtherSubject ID not found for removing user roles'),
            ));
        }
    }, [dispatch, otherSubjectId]);

    const removeConfirmTitle = useCallback((_id: number, user: ManagementOtherSubjectsTableType) =>
        t('confirmRemovingUserRoles', {user: user.username})
    , [t]);

    const handleSubmitModal = useCallback(() => {
        if (otherSubjectId.isFull) {
            const submitAction = isEdit
                ? adminAction.updateManagementOtherSubjectDetail
                : adminAction.createManagementOtherSubject;
            const safeOtherSubjectId = otherSubjectId.orCrash('OtherSubject ID not found');
            dispatch(submitAction(safeOtherSubjectId));
        }
    }, [isEdit, otherSubjectId, dispatch]);

    const handleOpenCreateModal = useCallback(() => {
        setActionMode('create');
        dispatch(adminAction.setManagementSubjectModalVisible(otherSubjectForm));
    }, [dispatch]);

    const handleOpenEditModal = useCallback(() => {
        setActionMode('edit');
        dispatch(adminAction.setManagementSubjectModalVisible(otherSubjectForm));
    }, [dispatch]);

    const handleDynamicCellClick = useCallback((userProfileOtherSubjectRoleId: number) => {
        dispatch(adminAction.getManagementOtherSubjectDetail(userProfileOtherSubjectRoleId));
        handleOpenEditModal();
    }, [dispatch, handleOpenEditModal]);

    const rowMapper = useCallback((row: ManagementOtherSubjectsTableType) =>
        managementOtherSubjectsRowMapper(row, handleDynamicCellClick),
    [handleDynamicCellClick]);

    const handleColumnOpenCreateModal = useCallback((columnRoleId: string, roleName: string) => {
        const roleIdNarrowed = opt(columnRoleId).map(Number).narrow(isNumber);

        if (roleIdNarrowed.isFull) {
            setUserProfileAndRoleIds({
                role: {
                    id: roleIdNarrowed.orCrash('Open create modal in other subjects: Role ID is not a number'),
                    name: roleName,
                },
                userProfile: null,
            });
            handleOpenCreateModal();
        }
    }, [handleOpenCreateModal]);

    const handleRowOpenCreateModal = useCallback((_id: number, record: ManagementOtherSubjectsTableType) => {
        setUserProfileAndRoleIds({
            role: null,
            userProfile: {
                id: record.userProfileId,
                name: record.username,
            },
        });
        handleOpenCreateModal();
    }, [handleOpenCreateModal]);

    const handleOpenCreateModalWithoutLimitations = useCallback(() => {
        setUserProfileAndRoleIds(defaultUserProfileAndRoleIds);
        handleOpenCreateModal();
    }, [handleOpenCreateModal]);

    const handleCloseModal = useCallback(() => {
        setActionMode(null);
        dispatch(adminAction.setManagementSubjectModalVisible(null));
        dispatch(adminAction.setManagementOtherSubjectsUsers([]));
        dispatch(adminAction.setManagementOtherSubjectsRoles([]));
    }, [dispatch]);

    const handleUserSearch = useCallback((searchText: string): void => {
        if (otherSubjectId.isFull) {
            dispatch(adminAction.searchManagementOtherSubjectsUsers(
                searchText,
                otherSubjectId.orCrash('User search in other subjects: Unknown other subject ID'),
            ));
        }
    }, [dispatch, otherSubjectId]);

    const handleRoleSearch = useCallback((searchText: string): void => {
        dispatch(adminAction.searchManagementOtherSubjectsRoles(searchText));
    }, [dispatch]);

    return {
        otherSubjectId,
        otherSubjectsOptions,
        managementOtherSubjectsExtraColumns,
        roleOptions,
        userProfileAndRoles,
        userProfileOptions,
        visibleModal,
        modalTitle,
        otherSubjectDetail,
        isEdit,
        t,
        tCommon,
        searchManagementOtherSubjects,
        handleManagementOtherSubjectSelectChangeValue,
        onManagementOtherSubjectsTableFetch,
        removeConfirmTitle,
        removeRolesForUser,
        removeUsersFromRole,
        handleCloseModal,
        handleColumnOpenCreateModal,
        handleOpenCreateModalWithoutLimitations,
        handleRoleSearch,
        handleRowOpenCreateModal,
        handleSubmitModal,
        handleUserSearch,
        validate: managementOtherSubjectsValidate,
        rowMapper,
        clearOtherSubjectDetail,
        validateDebounced,
    };
};
